import { faFileExport, faPrint } from '@fortawesome/free-solid-svg-icons';
import React, { useState, useEffect, useMemo } from 'react';
import { getCurrentCompany, getCompanyLogo, getHydrantFlushes } from '../../../services/api';
import CircleButton from '../../../components/CircleButton';
import LabelledTextBox from '../../../components/LabelledTextBox';
import { makeCompany, makeCompanyLogo, makeHydrantFlush } from '../../../models';
import FireFlowReport from '../../../components/fireFlowTests/FireFlowReport';
import { makeFireFlowReport } from '../../../models';
import { addDays } from '../../../utils/dates';
import FlushSummary from './FlushSummary';
import HydrantFlushReport from './HydrantFlushReport';

const HydrantFlushResultsReport = () => {
  const [hydrdantFlushes, setHydrantFlushes] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [company, setCompany] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [loadingHydrantFlushes, setLoadingHydrantFlushes] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filter, setFilter] = useState({
    startDate: new Date().getFullYear() + '-01-01',
    endDate: new Date().getFullYear() + '-12-31'
  });

  useEffect(() => {
    setupCompany();
    setupHydrantFlushes();
  }, []);
  useEffect(() => {
    setupCompanyLogo();
  }, [company]);
  useEffect(() => {
    filterReports();
  }, [filter, hydrdantFlushes]);
  useEffect(() => {
    setLoading(loadingCompany || loadingHydrantFlushes);
  }, [loadingCompany, loadingHydrantFlushes]);

  const setupCompany = async () => {
    try {
      setLoadingCompany(true);
      const fetchedCompany = await getCurrentCompany();
      setCompany(makeCompany(fetchedCompany));
      setLoadingCompany(false);
    } catch (ex) {
      console.log(ex);
      setLoadingCompany(false);
    }
  };

  const setupCompanyLogo = async () => {
    if (!company) return;
    const result = await getCompanyLogo(company.id);
    if (!result || result.deletedDate) return;
    setCompanyLogo(makeCompanyLogo(result));
  };

  const setupHydrantFlushes = async () => {
    try {
      setLoadingHydrantFlushes(true);
      console.log('Fetching Hydrant Flushes');
      const results = await getHydrantFlushes();
      console.log('got Hydrant Flushes', results);
      const reports = results.map(r => makeHydrantFlush(r));
      console.log('made Hydrant Flushes', reports);
      setHydrantFlushes(reports);
      setLoadingHydrantFlushes(false);
    } catch (ex) {
      setLoadingHydrantFlushes(false);
    }
  };

  const filterReports = () => {
    const filtered = hydrdantFlushes.filter(r => {
      return r.startDate
        && (!filter.startDate || new Date(r.startDate) >= new Date(`${filter.startDate}T00:00`))
        && (!filter.endDate || new Date(r.startDate) < addDays(1, new Date(`${filter.endDate}T00:00`)));
    });
    console.log(filteredReports);
    setFilteredReports(filtered);
  };

  const print = () => {
    window.print();
  };

  const onChange = e => {
    const type = e.target.type;
    let value = e.target.value;
    switch (type) {
      case "number":
        value = +value;
        break;
      // case "date":
      //   value = new Date(value + ' 0:0');
      //   break;
    }
    console.log(value);
    setFilter({ ...filter, [e.target.name]: value });
  };

  const onBlur = e => {
    console.log(e.target.type);
    const type = e.target.type;
    let value = e.target.value;
    switch (type) {
      case "number":
        value = +value;
        break;
      // case "date":
      //   value = new Date(value + ' 0:0');
      //   break;
    }

    setFilter({ ...filter, [e.target.name]: value });
  };

  const reportResults = useMemo(() => filteredReports.map((r, idx) => (
    <HydrantFlushReport key={idx} hydrantFlush={r} loading={false} />
  )), [filteredReports]);
  const phoneNumber = useMemo(() => company?.phoneNumber ? `(${company?.phoneNumber.substring(0, 3)}) ${company?.phoneNumber.substring(3, 6)}-${company?.phoneNumber.substring(6, company?.phoneNumber.length)}`.replace(/[\(\)\s-]+$/, '') : null, [company]);
  const totalGallonsUsed = useMemo(() => filteredReports.reduce((total, report) => total + (report.gallonsUsed || 0), 0), [filteredReports]);
  return (
    <div className="report flush-summary-report">
      <div className="report-filter no-print">
        <LabelledTextBox type="date" label="Start Date" value={filter.startDate} name="startDate" onChange={onChange} onBlur={onBlur}/>
        <LabelledTextBox type="date" label="End Date" value={filter.endDate} name="endDate" onChange={onChange} onBlur={onBlur}/>
      </div>
      {(loading || exporting) && <div className="spinner"></div>}
      <div className="report-title">
        <h2>Flush Results Report</h2>
        <div className="report-actions">
          {loading || <CircleButton icon={faPrint} size="large" title="Print" onClick={print} className="no-print" />}
        </div>
      </div>
      <table className="report-table">
        <thead>
          <tr>
            <td>
              <div className="report-header">
                {companyLogo ?
                  <div className="header-logo">
                    <img src={`${companyLogo?.dataType},${companyLogo?.image}`} alt={companyLogo.label} />
                  </div>
                : null}
                {company ?
                  <div className="header-details">
                    <div className="header-detail">
                      <span>{company?.name}</span>
                    </div>
                    <div className="header-detail">
                      <span>{company?.streetAddress}</span>
                    </div>
                    <div className="header-detail">
                      <span>{company?.city}{company?.city && company?.state ? ',' : null} </span>
                      <span>{company?.state} </span> 
                      <span>{company?.zipCode}</span>
                    </div>
                    {phoneNumber ?
                      <div className="header-detail">
                        <span>{phoneNumber}</span>
                      </div>
                    : null}
                  </div>
                : null}
              </div>
              <div>
              </div>
            </td>
          </tr>
          <FlushSummary
            companyName={company?.name}
            startDate={filter.startDate}
            endDate={filter.endDate}
            totalFlushes={filteredReports.length}
            totalGallonsUsed={totalGallonsUsed}
          />
        </thead>
        <tbody>
          {reportResults}
        </tbody>
      </table>
    </div>
  );
};

export default HydrantFlushResultsReport;